import PropTypes from 'prop-types'
import { MessageSquare, MapPin, TrendingUp, Clock } from 'lucide-react'
import { useLocation } from '../../contexts/LocationContext'

/**
 * CacheQueryCard Component
 *
 * Displays a single cached query entry
 */
export default function CacheQueryCard({ query, onClick }) {
  const {
    prompt,
    category,
    location,
    summary,
    cachedAt,
    usageCount,
    lastAccessed,
  } = query

  const { findLocationByCode } = useLocation()

  const getCategoryColor = cat => {
    return cat === 'health'
      ? 'bg-green-100 text-green-700 border-green-200'
      : 'bg-blue-100 text-blue-700 border-blue-200'
  }

  const formatDate = dateString => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    return date.toLocaleDateString()
  }

  const getLocationText = loc => {
    if (!loc) return 'Unknown'

    // Convert location codes to human-readable names
    const districtName = loc.district
      ? findLocationByCode(loc.district)?.name || loc.district
      : null
    const subcountyName = loc.subcounty
      ? findLocationByCode(loc.subcounty)?.name || loc.subcounty
      : null
    const parishName = loc.parish
      ? findLocationByCode(loc.parish)?.name || loc.parish
      : null
    const villageName = loc.village
      ? findLocationByCode(loc.village)?.name || loc.village
      : null

    const parts = [villageName, parishName, subcountyName, districtName].filter(
      Boolean
    )
    return parts.length > 0 ? parts.join(', ') : 'Unknown'
  }

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-3 bg-gray-50 hover:bg-blue-50 rounded-lg border border-gray-200 hover:border-blue-300 transition-all group"
    >
      <div className="flex items-start gap-3">
        <div className="p-2 bg-white rounded-lg shadow-sm group-hover:shadow-md transition-shadow">
          <MessageSquare className="w-4 h-4 text-gray-600 group-hover:text-blue-600 transition-colors" />
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          {/* Query Text */}
          <p className="text-sm font-medium text-gray-900 group-hover:text-blue-900 line-clamp-2">
            {prompt}
          </p>

          {/* Summary */}
          {summary && (
            <p className="text-xs text-gray-600 line-clamp-1">{summary}</p>
          )}

          {/* Metadata */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            {/* Category Badge */}
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-md font-medium border ${getCategoryColor(category)}`}
            >
              {category}
            </span>

            {/* Location */}
            {location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {getLocationText(location)}
              </span>
            )}

            {/* Usage Count */}
            {usageCount > 0 && (
              <span className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                {usageCount} use{usageCount !== 1 ? 's' : ''}
              </span>
            )}

            {/* Time */}
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDate(lastAccessed || cachedAt)}
            </span>
          </div>
        </div>
      </div>
    </button>
  )
}

CacheQueryCard.propTypes = {
  query: PropTypes.shape({
    cacheKey: PropTypes.string.isRequired,
    prompt: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    location: PropTypes.object,
    summary: PropTypes.string,
    cachedAt: PropTypes.string.isRequired,
    usageCount: PropTypes.number,
    lastAccessed: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
}
